/**
 * @fileoverview Mock backend for the prospect review (Approvals) endpoints.
 * @description Registers GET /approvals, GET /approvals/summary, GET /approvals/:id, POST
 * /approvals/:id/approve and /reject and POST /approvals/bulk-approve on the feature's shared mock adapter, answering in the backend's envelopes with its error
 * codes (Confluence 3.5.12). A review request is projected from a pending, rejected or withdrawn
 * submission in the shared Tenant store (see sales-crm-mock-projection.ts), routed to the maker's
 * Sales Head, or the Super Admin when the reporting line reaches none. Approving activates the
 * proposed owner; rejecting takes the submission out of every list. Decisions check the
 * X-Approval-Version header the way the backend does. Like the other sales mocks it doesn't check
 * MFA freshness. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  approvalRequestIdOf,
  approvalStatusOf,
  approvalVersionOf,
  decisionsOf,
  hasReviewRequest,
  mockCaller,
  ownerOf,
  recordDecision,
  reviewerOf,
  stateIdByName,
  toBackendRating,
  toProspectDetails,
  toProspectListItem,
} from './sales-crm-mock-projection'
import {
  bodyOf,
  failReply,
  headerOf,
  okEnvelope,
  pageReply,
  queryOf,
} from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import {
  approveTenant,
  findTenantById,
  listEveryTenant,
  recordTenantOwnershipChange,
} from './tenants-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type {
  ApprovalDetail,
  ApprovalListItem,
  ApprovalPerson,
  ApprovalRejectionReason,
} from '@/features/sales/types/approval.types'
import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

const REJECTION_REASONS: ApprovalRejectionReason[] = [
  'INVALID_DETAILS',
  'DUPLICATE_BANK',
  'OUTSIDE_BUSINESS_SCOPE',
  'OTHER',
]

/**
 * @description A roster user as a person on a review request.
 * @param {string | null | undefined} userId - The user's roster id.
 * @returns {ApprovalPerson | null} The person, or null when there's none.
 */
function personOf(userId: string | null | undefined): ApprovalPerson | null {
  const owner = ownerOf(userId)
  return owner ? { userId: owner.userId, displayName: owner.displayName } : null
}

/**
 * @description Whether a submission passes the review list's own filters: who submitted it, its
 * rating, and a literal search over the bank name, city and state.
 * @param {Tenant} tenant - The submission.
 * @param {Record<string, string | undefined>} query - The request's query parameters.
 * @returns {boolean} True when it matches every one that was sent.
 */
function matchesFilters(tenant: Tenant, query: Record<string, string | undefined>): boolean {
  const search = query.search?.trim().toLowerCase()
  const searched =
    !search ||
    [tenant.name, tenant.city, tenant.state].some((text) => text.toLowerCase().includes(search))
  return (
    searched &&
    (!query.submitterUserId || tenant.createdById === query.submitterUserId) &&
    (!query.leadRating || toBackendRating(tenant.leadRating) === query.leadRating)
  )
}

/**
 * @description A tenant's submission as a review list row.
 * @param {Tenant} tenant - The tenant.
 * @returns {ApprovalListItem} The row.
 */
function toApprovalListItem(tenant: Tenant): ApprovalListItem {
  return {
    approvalRequestId: approvalRequestIdOf(tenant.id),
    requestType: 'PROSPECT_CREATE',
    prospectId: tenant.id,
    tenantId: tenant.id,
    tenantCode: tenant.id,
    bankName: tenant.name,
    status: approvalStatusOf(tenant),
    subjectRevision: 1,
    submittedBy: personOf(tenant.createdById) ?? { userId: '', displayName: '—' },
    proposedOwner: personOf(tenant.requestedOwnerId),
    approver: personOf(reviewerOf(tenant)) ?? { userId: '', displayName: '—' },
    routingRevision: 1,
    requiresReassignment: false,
    submittedAt: tenant.createdAt,
    city: tenant.city,
    state: tenant.state,
    stateId: stateIdByName(tenant.state),
    branchCount: tenant.branches,
    leadRating: toBackendRating(tenant.leadRating),
    submitterPositionCode: ownerOf(tenant.createdById)?.positionCode ?? null,
    approvalVersion: approvalVersionOf(tenant),
  }
}

/**
 * @description A tenant's submission as a review request in full.
 * @param {Tenant} tenant - The tenant.
 * @returns {ApprovalDetail} The request.
 */
function toApprovalDetail(tenant: Tenant): ApprovalDetail {
  const details = toProspectDetails(tenant)
  const decisions = decisionsOf(tenant.id)
  return {
    ...toApprovalListItem(tenant),
    subject: {
      ...toProspectListItem(tenant),
      primaryContact: details.primaryContact,
      address: details.address,
      organisation: details.organisation,
      notes: details.notes,
    },
    decisions,
    routingHistory: [
      {
        routingRevision: 1,
        fromApproverUserId: null,
        toApproverUserId: reviewerOf(tenant),
        reason: 'INITIAL_ROUTING',
        createdAt: tenant.createdAt,
      },
    ],
    historyTotal: decisions.length + 1,
    historyTruncated: false,
    approvalVersion: approvalVersionOf(tenant),
  }
}

/**
 * @description Every submission that has a review request.
 * @returns {Tenant[]} Pending, decided and withdrawn submissions.
 */
function submissions(): Tenant[] {
  return listEveryTenant().filter(hasReviewRequest)
}

/**
 * @description Finds a review request's submission by request id.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {number} position - Where the id sits in the URL, counted from the end.
 * @returns {Tenant | undefined} The submission.
 */
function submissionFor(config: AxiosRequestConfig, position: number): Tenant | undefined {
  const requestId = decodeURIComponent(config.url?.split('/').at(position) ?? '')
  const tenant = findTenantById(requestId.replace(/^APR-/, ''))
  return tenant && hasReviewRequest(tenant) ? tenant : undefined
}

/**
 * @description Whether the caller may read a review request: its maker, its reviewer, or Super
 * Admin.
 * @param {Tenant} tenant - The submission.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @returns {boolean} True when they may.
 */
function canRead(tenant: Tenant, caller: SalesUserRecord | undefined): boolean {
  if (!caller) return false
  return (
    caller.role === 'Super Admin' ||
    tenant.createdById === caller.id ||
    reviewerOf(tenant) === caller.id
  )
}

/**
 * @description Checks who may decide a request, in the backend's order: never its maker, and only
 * its assigned reviewer or the Super Admin (who may decide any pending request).
 * @param {Tenant} tenant - The submission.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @returns {MockReply | null} The error to answer, or null when they may decide it.
 */
function authorityRefusal(tenant: Tenant, caller: SalesUserRecord): MockReply | null {
  if (tenant.createdById === caller.id) {
    return failReply(403, '5003', 'The maker cannot decide their own submission')
  }
  if (caller.role !== 'Super Admin' && reviewerOf(tenant) !== caller.id) {
    return failReply(403, '2008', 'Forbidden')
  }
  return null
}

/**
 * @description Checks the request is still pending and was read at its current version.
 * @param {Tenant} tenant - The submission.
 * @param {string | undefined} version - The version the caller sent.
 * @returns {MockReply | null} The error to answer, or null when the decision may go ahead.
 */
function stateRefusal(tenant: Tenant, version: string | undefined): MockReply | null {
  if (approvalStatusOf(tenant) !== 'PENDING') {
    return failReply(409, '5002', 'The approval request is not pending')
  }
  if (version !== approvalVersionOf(tenant)) {
    return failReply(412, '5011', 'The approval request changed since it was read')
  }
  return null
}

/**
 * @description Runs the checks every decision shares, in the backend's order.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {{ tenant: Tenant; caller: SalesUserRecord } | MockReply} The submission and caller, or
 * the error to answer.
 */
function checkDecision(
  config: AxiosRequestConfig,
): { tenant: Tenant; caller: SalesUserRecord } | MockReply {
  const caller = mockCaller()
  const tenant = submissionFor(config, -2)
  if (!caller || !tenant || !canRead(tenant, caller)) {
    return failReply(404, '5001', 'Approval request not found')
  }
  const refusal =
    authorityRefusal(tenant, caller) ??
    (headerOf(config, 'Idempotency-Key')
      ? null
      : failReply(428, '0004', 'Idempotency-Key is required')) ??
    stateRefusal(tenant, headerOf(config, 'X-Approval-Version'))
  return refusal ?? { tenant, caller }
}

/**
 * @description Approves a submission: it joins the backlog, its proposed owner takes effect and the
 * decision is recorded.
 * @param {Tenant} tenant - The submission.
 * @param {SalesUserRecord} caller - The approving caller.
 * @param {string | null} reviewNote - The optional note.
 */
function applyApproval(tenant: Tenant, caller: SalesUserRecord, reviewNote: string | null): void {
  approveTenant(tenant.id)
  if (tenant.requestedOwnerId) {
    recordTenantOwnershipChange(tenant.id, {
      fromOwnerId: null,
      toOwnerId: tenant.requestedOwnerId,
      actingUserId: caller.id,
      transferredAt: new Date().toISOString(),
    })
  }
  recordDecision(tenant.id, {
    decisionId: crypto.randomUUID(),
    decision: 'APPROVED',
    actorUserId: caller.id,
    reasonCode: null,
    reviewNote,
    subjectRevision: 1,
    routingRevision: 1,
    decidedAt: new Date().toISOString(),
  })
}

/**
 * @description Bulk Approve: decides each item on its own through the same checks as Approve, and
 * reports it as approved or skipped with the code Approve would have answered.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The approved and the skipped, or the backend's error.
 */
function bulkApproveReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  if (!caller || (caller.role !== 'Super Admin' && caller.role !== 'Sales Head')) {
    return failReply(403, '2008', 'Forbidden')
  }
  if (!headerOf(config, 'Idempotency-Key')) {
    return failReply(428, '0004', 'Idempotency-Key is required')
  }
  const { items, reviewNote } = bodyOf(config) as {
    items?: { approvalRequestId?: string; approvalVersion?: string }[]
    reviewNote?: string | null
  }
  const ids = (items ?? []).map((item) => item.approvalRequestId ?? '')
  if (!items || items.length < 1 || items.length > 50 || new Set(ids).size !== ids.length) {
    return failReply(400, '0001', 'A batch holds 1-50 distinct requests')
  }
  const approved: object[] = []
  const skipped: object[] = []
  for (const item of items) {
    const tenant = findTenantById((item.approvalRequestId ?? '').replace(/^APR-/, ''))
    const refusal =
      tenant && hasReviewRequest(tenant)
        ? (authorityRefusal(tenant, caller) ?? stateRefusal(tenant, item.approvalVersion))
        : failReply(404, '5001', 'Approval request not found')
    if (refusal || !tenant) {
      const { appCode, message } = (refusal?.[1] ?? {}) as { appCode?: string; message?: string }
      skipped.push({ approvalRequestId: item.approvalRequestId, appCode, message })
      continue
    }
    applyApproval(tenant, caller, reviewNote?.trim() || null)
    approved.push({
      approvalRequestId: approvalRequestIdOf(tenant.id),
      prospectId: tenant.id,
      approvalVersion: approvalVersionOf(tenant),
      replayed: false,
    })
  }
  return [200, okEnvelope({ approved, skipped })]
}

/**
 * @description Answers a decision the way the backend does.
 * @param {Tenant} tenant - The decided submission.
 * @returns {MockReply} The decision result.
 */
function decisionReply(tenant: Tenant): MockReply {
  const detail = toApprovalDetail(tenant)
  return [
    200,
    okEnvelope({
      approvalRequestId: detail.approvalRequestId,
      status: detail.status,
      prospectId: tenant.id,
      admissionState: detail.subject.admissionState,
      decidedAt: new Date().toISOString(),
      approvalVersion: detail.approvalVersion,
    }),
  ]
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/approvals').reply((config) => {
    const caller = mockCaller()
    const query = queryOf(config)
    const context = query.context ?? 'REVIEW'
    const status = query.status ?? 'PENDING'
    if (!caller) return failReply(403, '2008', 'Forbidden')
    if (context === 'OVERSIGHT' && caller.role !== 'Super Admin') {
      return failReply(403, '2008', 'Forbidden')
    }
    const rows = submissions()
      .filter((tenant) => approvalStatusOf(tenant) === status)
      .filter((tenant) => {
        if (context === 'SUBMITTED') return tenant.createdById === caller.id
        if (context === 'OVERSIGHT') return true
        return reviewerOf(tenant) === caller.id
      })
      .filter((tenant) => matchesFilters(tenant, query))
      .sort(
        (a, b) =>
          a.createdAt.localeCompare(b.createdAt) * (query.sortDirection === 'DESC' ? -1 : 1),
      )
      .map(toApprovalListItem)
    return pageReply(config, rows)
  })

  // Registered before GET /approvals/:id, which would otherwise answer /approvals/summary.
  salesMockAdapter.onGet('/approvals/summary').reply(() => {
    const caller = mockCaller()
    if (!caller || (caller.role !== 'Super Admin' && caller.role !== 'Sales Head')) {
      return failReply(403, '2008', 'Forbidden')
    }
    const pending = submissions().filter((tenant) => approvalStatusOf(tenant) === 'PENDING')
    /**
     * @description Looks up the position code of the Sales user who created a tenant.
     * @param {Tenant} tenant - The tenant.
     * @returns {string | undefined} The creator's position code, if the creator is known.
     */
    const positionOf = (tenant: Tenant) => ownerOf(tenant.createdById)?.positionCode
    return [
      200,
      okEnvelope({
        waiting: pending.length,
        fromManagers: pending.filter((tenant) => positionOf(tenant) === 'SALES_MANAGER').length,
        fromExecutives: pending.filter((tenant) => positionOf(tenant) === 'SALES_EXECUTIVE').length,
        hotLeads: pending.filter((tenant) => tenant.leadRating === 'Hot').length,
        evaluatedAt: new Date().toISOString(),
      }),
    ]
  })

  salesMockAdapter.onGet(/^\/approvals\/[^/]+$/).reply((config) => {
    const tenant = submissionFor(config, -1)
    if (!tenant || !canRead(tenant, mockCaller())) {
      return failReply(404, '5001', 'Approval request not found')
    }
    return [200, okEnvelope(toApprovalDetail(tenant))]
  })

  salesMockAdapter.onPost('/approvals/bulk-approve').reply(bulkApproveReply)

  salesMockAdapter.onPost(/^\/approvals\/[^/]+\/approve$/).reply((config) => {
    const checked = checkDecision(config)
    if (Array.isArray(checked)) return checked
    const { tenant, caller } = checked
    const { reviewNote } = bodyOf(config) as { reviewNote?: string | null }
    applyApproval(tenant, caller, reviewNote ?? null)
    return decisionReply(tenant)
  })

  salesMockAdapter.onPost(/^\/approvals\/[^/]+\/reject$/).reply((config) => {
    const checked = checkDecision(config)
    if (Array.isArray(checked)) return checked
    const { tenant, caller } = checked
    const { reasonCode, reviewNote } = bodyOf(config) as {
      reasonCode?: ApprovalRejectionReason
      reviewNote?: string
    }
    const note = reviewNote?.trim() ?? ''
    if (
      !reasonCode ||
      !REJECTION_REASONS.includes(reasonCode) ||
      note.length < 1 ||
      note.length > 500
    ) {
      return failReply(
        400,
        '0001',
        'A reason code and a review note of 1-500 characters are required',
      )
    }
    tenant.approval = 'Rejected'
    recordDecision(tenant.id, {
      decisionId: crypto.randomUUID(),
      decision: 'REJECTED',
      actorUserId: caller.id,
      reasonCode,
      reviewNote: note,
      subjectRevision: 1,
      routingRevision: 1,
      decidedAt: new Date().toISOString(),
    })
    return decisionReply(tenant)
  })
}
